import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { apiFetch, apiJson, ApiError } from '@/lib/api';
import { track } from '@/lib/analytics';
import type { ProfessionalDirectoryProfile } from '@/lib/types';

type ListingForm = {
  display_name: string;
  headline: string;
  bio: string;
  state: string;
  specialties: string;
  website_url: string;
  email_public: string;
};

function toPayload(form: ListingForm) {
  return {
    display_name: form.display_name.trim(),
    headline: form.headline.trim() || null,
    bio: form.bio.trim(),
    state: form.state.trim(),
    specialties: form.specialties
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
    website_url: form.website_url.trim(),
    email_public: form.email_public.trim() || null,
  };
}

function fromProfile(profile: ProfessionalDirectoryProfile): ListingForm {
  return {
    display_name: profile.display_name ?? "",
    headline: profile.headline ?? "",
    bio: profile.bio ?? "",
    state: profile.state ?? "",
    specialties: (profile.specialties ?? []).join(", "),
    website_url: profile.website_url ?? "",
    email_public: profile.email_public ?? "",
  };
}

function statusLabel(status: ProfessionalDirectoryProfile["status"]): string {
  if (status === "approved") return "Approved and live in directory";
  if (status === "rejected") return "Needs updates before approval";
  return "Pending review";
}

function ProDirectoryPage() {
  const { getToken } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [form, setForm] = useState<ListingForm>({
    display_name: "",
    headline: "",
    bio: "",
    state: "",
    specialties: "",
    website_url: "",
    email_public: "",
  });

  const profileQuery = useQuery({
    queryKey: ["professional-directory-profile"],
    queryFn: async (): Promise<ProfessionalDirectoryProfile | null> => {
      const res = await apiFetch(getToken, "/v1/professional/directory-profile");
      if (res.status === 404) return null;
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new ApiError(
          `Request failed: ${res.status} ${res.statusText}`,
          res.status,
          text,
        );
      }
      return (await res.json()) as ProfessionalDirectoryProfile;
    },
  });

  useEffect(() => {
    if (!profileQuery.data) return;
    setForm(fromProfile(profileQuery.data));
  }, [profileQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = toPayload(form);
      const method = profileQuery.data ? "PATCH" : "POST";
      return await apiJson<ProfessionalDirectoryProfile>(
        getToken,
        "/v1/professional/directory-profile",
        {
          method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
    },
    onSuccess: (profile) => {
      setError(null);
      setSavedMessage("Your listing has been submitted for review.");
      setForm(fromProfile(profile));
      profileQuery.refetch();
      track("Pro Directory Listing Submitted", { status: profile.status });
    },
    onError: (e: unknown) => {
      setSavedMessage(null);
      if (e instanceof ApiError && e.bodyText) {
        try {
          const parsed = JSON.parse(e.bodyText) as { detail?: string };
          setError(parsed.detail ?? "Could not save your listing.");
          return;
        } catch {
          // fallthrough
        }
      }
      setError("Could not save your listing.");
    },
  });

  const isApproved = profileQuery.data?.status === "approved";
  const bodyStyle = {
    fontFamily:
      "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  } as const;

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" style={bodyStyle}>
      <h1 className="text-2xl font-semibold text-gray-900 mb-2" style={bodyStyle}>
        Directory listing
      </h1>
      <p className="text-gray-500 text-lg mb-8" style={bodyStyle}>
        Share your practice so Harbor users can find and contact you.
      </p>

      {profileQuery.isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : null}

      {profileQuery.data ? (
        <div className="mb-6 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          <p className="font-semibold">Status: {statusLabel(profileQuery.data.status)}</p>
          {profileQuery.data.rejection_reason ? (
            <p className="mt-1">Reviewer notes: {profileQuery.data.rejection_reason}</p>
          ) : null}
        </div>
      ) : null}

      {savedMessage ? (
        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          {savedMessage}
        </div>
      ) : null}

      {error ? (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
          <input
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            disabled={isApproved || saveMutation.isPending}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Headline (optional)</label>
          <input
            value={form.headline}
            onChange={(e) => setForm({ ...form, headline: e.target.value })}
            disabled={isApproved || saveMutation.isPending}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">State</label>
          <input
            value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })}
            disabled={isApproved || saveMutation.isPending}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Specialties (comma-separated)
          </label>
          <input
            value={form.specialties}
            onChange={(e) => setForm({ ...form, specialties: e.target.value })}
            disabled={isApproved || saveMutation.isPending}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Co-parenting, Mediation, High-conflict divorce"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Bio</label>
          <textarea
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            disabled={isApproved || saveMutation.isPending}
            className="w-full min-h-32 border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Website</label>
          <input
            value={form.website_url}
            onChange={(e) => setForm({ ...form, website_url: e.target.value })}
            disabled={isApproved || saveMutation.isPending}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="https://example.com"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Public email (optional)
          </label>
          <input
            value={form.email_public}
            onChange={(e) => setForm({ ...form, email_public: e.target.value })}
            disabled={isApproved || saveMutation.isPending}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div className="pt-2">
          <button
            type="button"
            disabled={isApproved || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
            className="rounded-xl bg-[#018A9D] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saveMutation.isPending
              ? "Saving…"
              : profileQuery.data
                ? "Resubmit for review"
                : "Submit for review"}
          </button>
          {isApproved ? (
            <p className="text-xs text-gray-500 mt-2">
              Approved listings are currently locked. Contact support for edits.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/pro/directory")({
  component: ProDirectoryPage,
});
